import type { SquadArchetype } from '@signalclash/shared';

import type { AudioBus } from './AudioBus.js';

// Voice layer for Signal Clash. Sits on top of `AudioBus` (which owns the
// physical voice gain bus) and adds:
//
//   * a single point of dispatch for every planned voice line
//   * priority gating so a low-priority "Recon online" can never land over a
//     critical "Core integrity critical" alert
//   * per-name / per-category / per-archetype / per-squad cooldowns so the
//     game doesn't spam selection chatter on every click during a normal
//     8-minute match
//   * rotation pools for selection / move / attack / under-fire so the same
//     line doesn't repeat back-to-back
//   * one-shot flags for lines that must only fire once per match (core
//     integrity critical, first hostile signal detected)
//
// Voice is additive: SFX still fires from `Sfx.ts` as before. This class is
// instantiated once in `ClientApp` and reset (`resetForMatch`) at every
// match start so cooldowns and one-shots don't bleed across rematches.
//
// File-resolution: voice lines are loaded via `AudioBus.playVoice(name)`,
// which uses the same .mp3-then-.wav lookup the SFX path already uses, and
// silently drops missing files with a single dev-only console hint. The 58
// planned voice files all live in `apps/client/public/audio/` with the
// `voice_*` naming convention documented in `docs/voiceLines.md`.

// ─── Priority levels ───────────────────────────────────────────────────────
// CRITICAL  may interrupt anything below it.
// HIGH      may interrupt LOW / MEDIUM.
// MEDIUM    only plays if nothing equal-or-higher is currently on the bus.
// LOW       drops whenever anything is on the bus.
export const VOICE_PRIORITY = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  CRITICAL: 4,
} as const;
export type VoicePriority = (typeof VOICE_PRIORITY)[keyof typeof VOICE_PRIORITY];

// ─── Cue name constants ────────────────────────────────────────────────────
// Single source of truth for filenames so no other module has to hard-code
// `voice_*` strings. Filenames intentionally match the planned 58-file pack.
export const VOICE = {
  // AI Announcer — match lifecycle / pressure / loss / victory
  SIGNAL_LIVE: 'voice_signal_live',
  CORE_ONLINE: 'voice_core_online',
  DEPLOYMENT_AUTHORIZED: 'voice_deployment_authorized',
  HOSTILE_SIGNAL_DETECTED: 'voice_hostile_signal_detected',
  ENEMY_PUSH_INCOMING: 'voice_enemy_push_incoming',
  HOSTILES_APPROACHING_CORE: 'voice_hostiles_approaching_core',
  SQUAD_READY: 'voice_squad_ready',
  UPGRADE_AVAILABLE: 'voice_upgrade_available',
  SIGNAL_INCOME_INCREASED: 'voice_signal_income_increased',
  DEFENSE_LINE_HOLDING: 'voice_defense_line_holding',
  DEFENSE_LINE_WEAKENING: 'voice_defense_line_weakening',
  CORE_UNDER_ATTACK: 'voice_core_under_attack',
  SIGNAL_CORE_TAKING_DAMAGE: 'voice_signal_core_taking_damage',
  CORE_INTEGRITY_CRITICAL: 'voice_core_integrity_critical',
  SIGNAL_BREACHED: 'voice_signal_breached',
  SQUAD_LOST: 'voice_squad_lost',
  SIGNAL_SECURED: 'voice_signal_secured',
  SIGNAL_LOST: 'voice_signal_lost',
} as const;

// Match-start rotation: rotate one of these per match so we get the variety
// without forcing a 3-line sequence that delays the player into the game.
const MATCH_START_POOL: string[] = [
  VOICE.SIGNAL_LIVE,
  VOICE.CORE_ONLINE,
  VOICE.DEPLOYMENT_AUTHORIZED,
];

// Per-archetype rotation pools (no-immediate-repeat, walked in order).
const SELECT_POOL: Record<SquadArchetype, string[]> = {
  recon: ['voice_recon_select_01', 'voice_recon_select_02', 'voice_recon_select_03'],
  assault: ['voice_assault_select_01', 'voice_assault_select_02', 'voice_assault_select_03'],
  engineer: ['voice_engineer_select_01', 'voice_engineer_select_02', 'voice_engineer_select_03'],
  shield: ['voice_shield_select_01', 'voice_shield_select_02', 'voice_shield_select_03'],
};

const MOVE_POOL: Record<SquadArchetype, string[]> = {
  recon: ['voice_recon_move_01', 'voice_recon_move_02', 'voice_recon_move_03'],
  assault: ['voice_assault_move_01', 'voice_assault_move_02', 'voice_assault_move_03'],
  engineer: ['voice_engineer_move_01', 'voice_engineer_move_02', 'voice_engineer_move_03'],
  shield: ['voice_shield_move_01', 'voice_shield_move_02', 'voice_shield_move_03'],
};

const ATTACK_POOL: Record<SquadArchetype, string[]> = {
  recon: ['voice_recon_attack_01', 'voice_recon_attack_02'],
  assault: ['voice_assault_attack_01', 'voice_assault_attack_02'],
  engineer: ['voice_engineer_attack_01', 'voice_engineer_attack_02'],
  shield: ['voice_shield_attack_01', 'voice_shield_attack_02'],
};

const UNDER_FIRE_BY_ARCHETYPE: Record<SquadArchetype, string> = {
  recon: 'voice_recon_under_fire_01',
  assault: 'voice_assault_under_fire_01',
  engineer: 'voice_engineer_under_fire_01',
  shield: 'voice_shield_under_fire_01',
};

const LOST_BY_ARCHETYPE: Record<SquadArchetype, string> = {
  recon: 'voice_recon_lost_01',
  assault: 'voice_assault_lost_01',
  engineer: 'voice_engineer_lost_01',
  shield: 'voice_shield_lost_01',
};

// Two-cue rotation for the announcer's "core taking damage" line so the same
// sample doesn't repeat each time the cooldown elapses.
const CORE_DAMAGE_POOL: string[] = [
  VOICE.CORE_UNDER_ATTACK,
  VOICE.SIGNAL_CORE_TAKING_DAMAGE,
];

// ─── Cooldown defaults ─────────────────────────────────────────────────────
// Single source of truth for every cooldown value. Numbers are tuned per the
// V1 voice-pack spec; bumping these here is the canonical knob.
export const VOICE_COOLDOWN_MS = {
  SELECT_GLOBAL: 1800,
  SELECT_PER_ARCHETYPE: 7000,
  MOVE_GLOBAL: 5000,
  MOVE_PER_ARCHETYPE: 11000,
  ATTACK_GLOBAL: 5000,
  ATTACK_PER_ARCHETYPE: 9000,
  UNDER_FIRE_PER_SQUAD: 11000,
  UNDER_FIRE_PER_ARCHETYPE: 13000,
  SQUAD_LOST_GLOBAL: 7000,
  SQUAD_LOST_PER_ARCHETYPE: 9000,
  SQUAD_READY: 12000,
  CORE_DAMAGE: 17000,
  RELAY_LOST: 9000,
  ENEMY_PUSH_INCOMING: 30000,
  HOSTILES_APPROACHING_CORE: 25000,
  DEFENSE_WEAKENING: 30000,
  DEFENSE_HOLDING: 50000,
  INCOME_INCREASED: 35000,
  // Move / attack chatter suppresses if a HIGH or CRITICAL voice played in
  // the last N ms — "Recon online" landing 300 ms after "Core under attack"
  // reads as the AI ignoring the player's situation.
  COMBAT_SUPPRESS_MOVE: 2000,
} as const;

// ─── Cooldown key helpers ─────────────────────────────────────────────────
// Cooldowns are keyed by a string so we can register multiple buckets per
// trigger (global + archetype + per-squad) without bookkeeping a Map for each.
// Constructing the key strings here keeps the call sites readable.
const KEY_SELECT_GLOBAL = 'select_global';
const KEY_MOVE_GLOBAL = 'move_global';
const KEY_ATTACK_GLOBAL = 'attack_global';
const KEY_SQUAD_LOST_GLOBAL = 'squad_lost_global';
const keySelectArchetype = (a: SquadArchetype): string => `select:${a}`;
const keyMoveArchetype = (a: SquadArchetype): string => `move:${a}`;
const keyAttackArchetype = (a: SquadArchetype): string => `attack:${a}`;
const keyUnderFireSquad = (id: number | string): string => `under_fire:${id}`;
const keyUnderFireArchetype = (a: SquadArchetype): string => `under_fire:${a}`;
const keySquadLostArchetype = (a: SquadArchetype): string => `squad_lost:${a}`;

interface PlayOptions {
  priority: VoicePriority;
  cooldownKeys?: Array<[string, number]>;
  // Suppress this line if a HIGH or CRITICAL voice landed in the last
  // COMBAT_SUPPRESS_MOVE window. Used by move / attack chatter so order
  // acknowledgements don't compete with combat narration.
  suppressIfRecentCombat?: boolean;
  // Override default priority gate; CRITICAL lines pass this so they always
  // interrupt whatever is on the bus.
  interrupt?: boolean;
}

export class Voice {
  private rotation = new Map<string, number>();
  private cooldowns = new Map<string, number>();
  private muted = false;
  private debug = false;
  // Timestamp of the most recent HIGH/CRITICAL voice. Move / attack chatter
  // consults this to suppress if combat narration just landed.
  private lastHighPriorityAt = 0;
  // One-shots that must only fire once per match.
  private hostileDetectedFired = false;
  private coreCriticalFired = false;
  // Rotation index for core-damage two-cue alternation (separate from the
  // per-pool rotation map so the cooldown gate doesn't reset the alternation).
  private coreDamageRotation = -1;
  // Pressure tracking for defense_line_holding gating. The "holding" line
  // should only fire AFTER a weakening event has happened — otherwise it
  // sounds like random flavour chatter.
  private hasFiredDefenseWeakening = false;

  constructor(private readonly bus: AudioBus) {}

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) this.bus.stopVoice();
  }

  // Optional debug logging — off by default, flipped via the dev console
  // (`__sigclash_voiceDebug(true)`) when triaging cooldown / priority drops.
  setDebug(d: boolean): void {
    this.debug = d;
  }

  // Reset all per-match state. Called from Sfx.detach() (which runs at every
  // match end) and from ClientApp.startMatch (defensive — cheap and idempotent).
  resetForMatch(): void {
    this.cooldowns.clear();
    this.rotation.clear();
    this.hostileDetectedFired = false;
    this.coreCriticalFired = false;
    this.coreDamageRotation = -1;
    this.hasFiredDefenseWeakening = false;
    this.lastHighPriorityAt = 0;
    this.bus.stopVoice();
  }

  // ─── Core dispatch ───────────────────────────────────────────────────────
  // Returns true if the line actually fired, false if it was dropped by
  // cooldown / priority / combat suppression. Callers don't need the return
  // value for normal operation; it's surfaced for tests and the debug log.
  playVoice(name: string, opts: PlayOptions): boolean {
    if (this.muted) return false;
    const now = performance.now();

    if (opts.suppressIfRecentCombat === true) {
      if (now - this.lastHighPriorityAt < VOICE_COOLDOWN_MS.COMBAT_SUPPRESS_MOVE) {
        this.log('suppress (recent combat)', name);
        return false;
      }
    }

    if (opts.cooldownKeys !== undefined) {
      for (const [key] of opts.cooldownKeys) {
        const until = this.cooldowns.get(key) ?? 0;
        if (until > now) {
          this.log('cooldown', name, key);
          return false;
        }
      }
    }

    const currentP = this.bus.getCurrentVoicePriority();
    if (currentP !== null && opts.interrupt !== true) {
      // Strictly-higher priority can interrupt. Equal-or-lower drops so we
      // don't cut "Core integrity critical" off with a second critical alert.
      if (opts.priority <= currentP) {
        this.log('dropped (lower-or-equal priority)', name);
        return false;
      }
    }

    if (opts.cooldownKeys !== undefined) {
      for (const [key, ms] of opts.cooldownKeys) {
        this.cooldowns.set(key, now + ms);
      }
    }

    if (opts.priority >= VOICE_PRIORITY.HIGH) {
      this.lastHighPriorityAt = now;
    }

    this.log('play', name);
    void this.bus.playVoice(name, { priority: opts.priority });
    return true;
  }

  // ─── High-level squad routing ────────────────────────────────────────────
  // These are the entry points wired from ClientApp / InputRouter / Sfx for
  // viewer-owned squad events. They handle rotation, cooldowns, and pool
  // lookup so callers just need to know the archetype.

  playSelection(archetype: SquadArchetype): void {
    const name = this.nextInPool(SELECT_POOL[archetype], `select:${archetype}`);
    this.playVoice(name, {
      priority: VOICE_PRIORITY.LOW,
      cooldownKeys: [
        [KEY_SELECT_GLOBAL, VOICE_COOLDOWN_MS.SELECT_GLOBAL],
        [keySelectArchetype(archetype), VOICE_COOLDOWN_MS.SELECT_PER_ARCHETYPE],
      ],
    });
  }

  playMoveOrder(archetype: SquadArchetype): void {
    const name = this.nextInPool(MOVE_POOL[archetype], `move:${archetype}`);
    this.playVoice(name, {
      priority: VOICE_PRIORITY.LOW,
      cooldownKeys: [
        [KEY_MOVE_GLOBAL, VOICE_COOLDOWN_MS.MOVE_GLOBAL],
        [keyMoveArchetype(archetype), VOICE_COOLDOWN_MS.MOVE_PER_ARCHETYPE],
      ],
      suppressIfRecentCombat: true,
    });
  }

  playAttackOrder(archetype: SquadArchetype): void {
    const name = this.nextInPool(ATTACK_POOL[archetype], `attack:${archetype}`);
    this.playVoice(name, {
      priority: VOICE_PRIORITY.LOW,
      cooldownKeys: [
        [KEY_ATTACK_GLOBAL, VOICE_COOLDOWN_MS.ATTACK_GLOBAL],
        [keyAttackArchetype(archetype), VOICE_COOLDOWN_MS.ATTACK_PER_ARCHETYPE],
      ],
    });
  }

  playUnderFire(archetype: SquadArchetype, squadId: number | string): void {
    this.playVoice(UNDER_FIRE_BY_ARCHETYPE[archetype], {
      priority: VOICE_PRIORITY.MEDIUM,
      cooldownKeys: [
        [keyUnderFireSquad(squadId), VOICE_COOLDOWN_MS.UNDER_FIRE_PER_SQUAD],
        [keyUnderFireArchetype(archetype), VOICE_COOLDOWN_MS.UNDER_FIRE_PER_ARCHETYPE],
      ],
    });
  }

  playSquadLost(archetype: SquadArchetype | null): void {
    // Archetype-specific death takes priority. When the killed squad's
    // archetype is unknown (defensive — Sfx always passes one in practice)
    // fall back to the generic announcer line.
    if (archetype !== null) {
      this.playVoice(LOST_BY_ARCHETYPE[archetype], {
        priority: VOICE_PRIORITY.HIGH,
        cooldownKeys: [
          [KEY_SQUAD_LOST_GLOBAL, VOICE_COOLDOWN_MS.SQUAD_LOST_GLOBAL],
          [keySquadLostArchetype(archetype), VOICE_COOLDOWN_MS.SQUAD_LOST_PER_ARCHETYPE],
        ],
      });
      return;
    }
    this.playVoice(VOICE.SQUAD_LOST, {
      priority: VOICE_PRIORITY.HIGH,
      cooldownKeys: [[KEY_SQUAD_LOST_GLOBAL, VOICE_COOLDOWN_MS.SQUAD_LOST_GLOBAL]],
    });
  }

  // Fired when multiple viewer-owned squads die inside a short window — the
  // generic "Squad lost" announcer line lands on top of the archetype lines.
  playMultipleSquadLost(): void {
    this.playVoice(VOICE.SQUAD_LOST, {
      priority: VOICE_PRIORITY.HIGH,
      cooldownKeys: [[KEY_SQUAD_LOST_GLOBAL, VOICE_COOLDOWN_MS.SQUAD_LOST_GLOBAL]],
    });
  }

  // ─── Announcer entry points ──────────────────────────────────────────────

  playMatchStart(matchIndex: number): void {
    // Rotate the three start cues per match so the same line isn't the
    // gameplay onramp every time. `matchIndex` is the session's match counter.
    const name = MATCH_START_POOL[matchIndex % MATCH_START_POOL.length]!;
    this.playVoice(name, {
      priority: VOICE_PRIORITY.MEDIUM,
    });
  }

  playSquadReady(): void {
    this.playVoice(VOICE.SQUAD_READY, {
      priority: VOICE_PRIORITY.LOW,
      cooldownKeys: [['squad_ready', VOICE_COOLDOWN_MS.SQUAD_READY]],
    });
  }

  playHostileSignalDetected(): void {
    if (this.hostileDetectedFired) return;
    const fired = this.playVoice(VOICE.HOSTILE_SIGNAL_DETECTED, {
      priority: VOICE_PRIORITY.MEDIUM,
    });
    if (fired) this.hostileDetectedFired = true;
  }

  playEnemyPushIncoming(): void {
    this.playVoice(VOICE.ENEMY_PUSH_INCOMING, {
      priority: VOICE_PRIORITY.MEDIUM,
      cooldownKeys: [['enemy_push_incoming', VOICE_COOLDOWN_MS.ENEMY_PUSH_INCOMING]],
    });
  }

  playHostilesApproachingCore(): void {
    this.playVoice(VOICE.HOSTILES_APPROACHING_CORE, {
      priority: VOICE_PRIORITY.HIGH,
      cooldownKeys: [['hostiles_approaching_core', VOICE_COOLDOWN_MS.HOSTILES_APPROACHING_CORE]],
    });
  }

  playCoreDamage(): void {
    // Two-cue rotation so the same line doesn't repeat every 17 s.
    this.coreDamageRotation = (this.coreDamageRotation + 1) % CORE_DAMAGE_POOL.length;
    const name = CORE_DAMAGE_POOL[this.coreDamageRotation]!;
    this.playVoice(name, {
      priority: VOICE_PRIORITY.HIGH,
      cooldownKeys: [['core_damage', VOICE_COOLDOWN_MS.CORE_DAMAGE]],
    });
  }

  playCoreIntegrityCritical(): void {
    if (this.coreCriticalFired) return;
    const fired = this.playVoice(VOICE.CORE_INTEGRITY_CRITICAL, {
      priority: VOICE_PRIORITY.CRITICAL,
      interrupt: true,
    });
    if (fired) this.coreCriticalFired = true;
  }

  playDefenseLineWeakening(): void {
    const fired = this.playVoice(VOICE.DEFENSE_LINE_WEAKENING, {
      priority: VOICE_PRIORITY.MEDIUM,
      cooldownKeys: [['defense_weakening', VOICE_COOLDOWN_MS.DEFENSE_WEAKENING]],
    });
    if (fired) this.hasFiredDefenseWeakening = true;
  }

  playDefenseLineHolding(): void {
    if (!this.hasFiredDefenseWeakening) return;
    this.playVoice(VOICE.DEFENSE_LINE_HOLDING, {
      priority: VOICE_PRIORITY.LOW,
      cooldownKeys: [['defense_holding', VOICE_COOLDOWN_MS.DEFENSE_HOLDING]],
    });
  }

  playSignalIncomeIncreased(): void {
    this.playVoice(VOICE.SIGNAL_INCOME_INCREASED, {
      priority: VOICE_PRIORITY.LOW,
      cooldownKeys: [['income_increased', VOICE_COOLDOWN_MS.INCOME_INCREASED]],
    });
  }

  // Future-safe hook: there is no upgrade system in V1 (and we explicitly
  // refuse to fake one for voice). When an upgrade flow lands, the trigger
  // site can call this directly without any other plumbing changes.
  playUpgradeAvailable(): void {
    this.playVoice(VOICE.UPGRADE_AVAILABLE, {
      priority: VOICE_PRIORITY.LOW,
      cooldownKeys: [['upgrade_available', 20000]],
    });
  }

  playMatchVictory(): void {
    this.playVoice(VOICE.SIGNAL_SECURED, {
      priority: VOICE_PRIORITY.CRITICAL,
      interrupt: true,
    });
  }

  playMatchDefeat(opts?: { coreDestroyed?: boolean }): void {
    // Hard defeat (core destroyed) uses the rarer "signal lost" line so the
    // outcome reads distinctly from a dominance/overtime loss.
    const name = opts?.coreDestroyed === true ? VOICE.SIGNAL_LOST : VOICE.SIGNAL_BREACHED;
    this.playVoice(name, {
      priority: VOICE_PRIORITY.CRITICAL,
      interrupt: true,
    });
  }

  // Cooldown-only register for relay-lost cue: there is no dedicated voice
  // line in the V1 pack for "relay lost" (the existing SFX `relay_lost.mp3`
  // handles that), but we still consume a "pressure" event for
  // defense-line-weakening detection in Sfx.ts.

  private nextInPool(pool: string[], poolKey: string): string {
    const last = this.rotation.get(poolKey);
    const next = last === undefined ? 0 : (last + 1) % pool.length;
    this.rotation.set(poolKey, next);
    return pool[next]!;
  }

  private log(reason: string, name: string, key?: string): void {
    if (!this.debug) return;
    // eslint-disable-next-line no-console
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug(`[voice] ${reason}: ${name}${key !== undefined ? ` (${key})` : ''}`);
    }
  }
}
